/**
 * Permission Guard Hook
 * Provides UI-level permission checking to prevent 403 errors
 */

import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import type { UserPermission } from '../../../types/auth';

export interface PermissionGuardResult {
  hasPermission: boolean;
  isLoading: boolean;
  checkPermission: (permission: string) => boolean;
  checkAnyPermission: (permissions: string[]) => boolean;
  checkAllPermissions: (permissions: string[]) => boolean;
  /**
   * True iff the user holds any permission that qualifies as admin panel
   * access per the verb-based rule (see isAdminPanelPermission).
   */
  hasAdminPanelAccess: boolean;
}

/**
 * Hook to check user permissions before rendering UI components
 * Prevents 403 errors by checking permissions client-side
 */
export const usePermissionGuard = (): PermissionGuardResult => {
  const { user, isLoading } = useSelector((state: RootState) => state.auth);
  const userPermissions = user?.systemPermissions || [];

  const checkPermission = (permission: string): boolean => {
    if (isLoading || !user) return false;
    
    // Debug logging
    console.log('🔍 Permission Check:', {
      permission,
      hasPermission: userPermissions.includes(permission),
      userPermissions,
      userEmail: user.email
    });
    
    return userPermissions.includes(permission);
  };

  const checkAnyPermission = (permissions: string[]): boolean => {
    if (isLoading || !user) return false;
    return permissions.some(permission => userPermissions.includes(permission));
  };

  const checkAllPermissions = (permissions: string[]): boolean => {
    if (isLoading || !user) return false;
    return permissions.every(permission => userPermissions.includes(permission));
  };

  // Normalize a UserPermission (string | {resource, actions}) into the
  // flat "<resource>.<action>" code format that isAdminPanelPermission expects.
  const toPermissionCodes = (p: UserPermission): string[] => {
    if (typeof p === 'string') return [p];
    return p.actions.map(action => `${p.resource}.${action}`);
  };

  const hasAdminPanelAccess =
    !isLoading
    && !!user
    && userPermissions.some((p: UserPermission) =>
      toPermissionCodes(p).some(isAdminPanelPermission)
    );

  return {
    hasPermission: userPermissions.length > 0,
    isLoading,
    checkPermission,
    checkAnyPermission,
    checkAllPermissions,
    hasAdminPanelAccess,
  };
};

/**
 * Verbs that indicate a mutating (write/configure) action. Any permission whose
 * action part (after the last `.`) is in this set grants admin panel access,
 * except those in ADMIN_PANEL_EXCLUDED_NAMESPACES.
 *
 * Rule (single source of truth for admin panel visibility):
 *   "User sees the admin panel iff they hold any permission whose verb is
 *    a write-verb AND the permission is not in a self-service / domain namespace."
 *
 * Read verbs (`read`, `view_*`) are deliberately excluded — e.g. `sys.users.read`
 * lets a user view their own tenant's user list but is not admin panel material.
 *
 * Permissions are created/edited via DB only (no UI), so the verb vocabulary is
 * controlled and stable. If a new verb is introduced it must be added here.
 */
export const ADMIN_PANEL_WRITE_VERBS: ReadonlySet<string> = new Set([
  'create',
  'update',
  'delete',
  'manage',
  'revoke',
  'send',
  'send_bulk',
  'manage_config',
]);

/**
 * Namespaces excluded from the admin panel gate even if they contain write verbs.
 * - `sys.profile.*` — self-service (manage your own profile)
 * - `party.*`      — tenant/domain management, has its own dedicated pages
 */
export const ADMIN_PANEL_EXCLUDED_NAMESPACES: ReadonlyArray<string> = [
  'sys.profile.',
  'party.',
];

/**
 * Pure function — returns true iff the given permission code should count
 * toward admin panel access.
 *
 * Examples:
 *   sys.users.update            → true
 *   sys.users.read              → false
 *   sys.notification.send_bulk  → true
 *   tenants.manage              → true
 *   party.manage                → false (excluded namespace)
 *   sys.profile.manage          → false (excluded namespace — self service)
 *   sys.apikey.manage           → true
 */
export const isAdminPanelPermission = (permissionCode: string): boolean => {
  if (!permissionCode) return false;
  if (ADMIN_PANEL_EXCLUDED_NAMESPACES.some(ns => permissionCode.startsWith(ns))) {
    return false;
  }
  const dot = permissionCode.lastIndexOf('.');
  if (dot < 0) return false;
  const verb = permissionCode.substring(dot + 1);
  return ADMIN_PANEL_WRITE_VERBS.has(verb);
};

/**
 * Permission requirements for different admin features
 */
export const ADMIN_PERMISSIONS = {
  // User Management
  USERS_VIEW: 'sys.users.read',
  USERS_CREATE: 'sys.users.create', 
  USERS_EDIT: 'sys.users.update',
  USERS_DELETE: 'sys.users.delete',
  USERS_MANAGE: ['sys.users.read', 'sys.users.update'] as string[], // View + Edit for user management

  // Role Management  
  ROLES_MANAGE: 'sys.roles.manage',

  // Permission Management
  PERMISSIONS_MANAGE: 'sys.permissions.manage',

  // System Features
  AUDIT_VIEW: 'sys.audit.read',
  CONFIG_MANAGE: 'sys.config.manage'
} as const;

/**
 * Helper hook for specific admin features
 */
export const useAdminPermissions = () => {
  const { checkPermission, checkAllPermissions, isLoading } = usePermissionGuard();

  return {
    // User Management Permissions
    canViewUsers: checkPermission(ADMIN_PERMISSIONS.USERS_VIEW),
    canCreateUsers: checkPermission(ADMIN_PERMISSIONS.USERS_CREATE),
    canEditUsers: checkPermission(ADMIN_PERMISSIONS.USERS_EDIT),
    canDeleteUsers: checkPermission(ADMIN_PERMISSIONS.USERS_DELETE),
    canManageUsers: checkAllPermissions(ADMIN_PERMISSIONS.USERS_MANAGE),

    // Role Management Permissions
    canManageRoles: checkPermission(ADMIN_PERMISSIONS.ROLES_MANAGE),

    // Permission Management Permissions  
    canManagePermissions: checkPermission(ADMIN_PERMISSIONS.PERMISSIONS_MANAGE),

    // System Permissions
    canViewAudit: checkPermission(ADMIN_PERMISSIONS.AUDIT_VIEW),
    canManageConfig: checkPermission(ADMIN_PERMISSIONS.CONFIG_MANAGE),

    // Loading state
    isLoading
  };
};